import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Press, haptic } from '@dorixona/ui'
import { isApiError } from '@dorixona/api'
import { Sheet } from '../../ui/Sheet'
import { Icon } from '../../ui/Icon'
import { failureText } from '../../ui/Load'
import { Field } from '../auth/ui/Field'
import { useAddCard, useConfirmCard, type CardChallenge, type SavedCard } from './cards'
import { cardDigits, cardErrorKey, cardReady, formatCardNumber, formatExpire } from './model'

export interface AddCardSheetProps {
  opened: boolean
  onClose: () => void
  /** Карта подтверждена — оформление сразу выбирает её для оплаты. */
  onAdded?: (card: SavedCard) => void
}

/**
 * Привязка карты: номер и срок, затем код из СМС банка. Код истёк — начинаем
 * заново с номера: так требует банк (otp_expired).
 */
export function AddCardSheet({ opened, onClose, onAdded }: AddCardSheetProps) {
  const { t } = useTranslation(['checkout', 'common'])
  const add = useAddCard()
  const confirm = useConfirmCard()
  const [number, setNumber] = useState('')
  const [expire, setExpire] = useState('')
  const [code, setCode] = useState('')
  const [challenge, setChallenge] = useState<CardChallenge | null>(null)
  const [error, setError] = useState('')

  const errorText = (failure: unknown) => {
    const key = isApiError(failure) ? cardErrorKey(failure.code) : ''
    return key ? t(key) : failureText(failure, t)
  }

  const close = () => {
    setNumber('')
    setExpire('')
    setCode('')
    setChallenge(null)
    setError('')
    onClose()
  }

  const send = () => {
    setError('')
    add.mutate(
      { number, expire },
      {
        onSuccess: (next) => {
          setChallenge(next)
        },
        onError: (failure) => {
          haptic('error')
          setError(errorText(failure))
        },
      },
    )
  }

  const verify = (current: CardChallenge) => {
    setError('')
    confirm.mutate(
      { cardId: current.cardId, code },
      {
        onSuccess: (card) => {
          haptic('success')
          onAdded?.(card)
          close()
        },
        onError: (failure) => {
          haptic('error')
          setError(errorText(failure))
          // Истёкший код не повторить: банк ждёт новую привязку с номера.
          if (isApiError(failure) && failure.code === 'otp_expired') {
            setChallenge(null)
            setCode('')
          }
        },
      },
    )
  }

  return (
    <Sheet opened={opened} label={t('checkout:cards.add')} onClose={close}>
      <div className="fsheet">
        <div className="fhead">
          <h2>{t('checkout:cards.add')}</h2>
          <Press
            squish="icon"
            className="fclose"
            aria-label={t('common:actions.close')}
            onPress={close}
          >
            <Icon name="i-x" size={18} />
          </Press>
        </div>

        {challenge ? (
          <div className="cardform">
            <p className="cardform-lead">
              {t('checkout:cards.codeSent', { phone: challenge.phone })}
            </p>
            <Field label={t('checkout:cards.code')} error={error || undefined}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  className="num"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-describedby={describedBy}
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                  }}
                />
              )}
            </Field>
            <div className="ffoot">
              <Button
                disabled={code.length < 4 || confirm.isPending}
                onPress={() => {
                  verify(challenge)
                }}
              >
                {confirm.isPending ? t('checkout:cards.checking') : t('checkout:cards.confirm')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="cardform">
            <Field label={t('checkout:cards.number')}>
              {({ id }) => (
                <input
                  id={id}
                  className="num"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  placeholder="0000 0000 0000 0000"
                  value={formatCardNumber(number)}
                  onChange={(event) => {
                    setNumber(cardDigits(event.target.value))
                  }}
                />
              )}
            </Field>
            <Field
              label={t('checkout:cards.expire')}
              hint={error ? undefined : t('checkout:cards.hint')}
              error={error || undefined}
            >
              {({ id, describedBy }) => (
                <input
                  id={id}
                  className="num cardform-exp"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  placeholder={t('checkout:cards.expireHint')}
                  aria-describedby={describedBy}
                  value={expire}
                  onChange={(event) => {
                    setExpire(formatExpire(event.target.value))
                  }}
                />
              )}
            </Field>
            <div className="ffoot">
              <Button disabled={!cardReady(number, expire) || add.isPending} onPress={send}>
                {add.isPending ? t('checkout:cards.sending') : t('checkout:cards.next')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  )
}
